import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, Power } from "lucide-react";
import { listPrograms, createProgram, updateProgram, deleteProgram, reorderPrograms } from "../adminApi";

const empty = { name: "", slug: "", category: "program", duration: "", mode: "", fee: "", feeNote: "", active: true, curriculum: [], eligibility: [] };

export default function Programs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // record or "new"
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await listPrograms()); } catch { toast.error("Failed to load programs."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = async (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try { await reorderPrograms(next.map((p) => p.id)); } catch { toast.error("Reorder failed."); load(); }
  };

  const toggleActive = async (p) => {
    try { await updateProgram(p.id, { ...p, active: !p.active }); load(); toast.success(p.active ? "Deactivated" : "Activated"); }
    catch (e) { toast.error(e.message); }
  };

  const remove = async () => {
    try { await deleteProgram(confirmDel.id); setConfirmDel(null); load(); toast.success("Program deleted."); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div data-testid="admin-programs">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Programs</h1>
          <p className="text-sm text-slate-500">Manage programs & internships, fees, order and visibility.</p>
        </div>
        <button onClick={() => setEditing("new")} data-testid="program-add" className="inline-flex items-center gap-2 rounded-lg bg-orange px-4 py-2 text-sm font-medium text-white hover:opacity-90">
          <Plus className="h-4 w-4" /> Add Program
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>
      ) : (
        <div className="space-y-3">
          {items.map((p, i) => (
            <div key={p.id} data-testid="program-item" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <button onClick={() => move(i, -1)} className="text-slate-400 hover:text-slate-700" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={() => move(i, 1)} className="text-slate-400 hover:text-slate-700" aria-label="Move down"><ArrowDown className="h-4 w-4" /></button>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{p.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{p.active ? "Active" : "Inactive"}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase text-slate-500">{p.category}</span>
                  </div>
                  <div className="text-xs text-slate-500">{p.duration} · {p.mode} · {p.fee}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleActive(p)} data-testid="program-toggle" className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50" aria-label="Toggle active"><Power className="h-4 w-4" /></button>
                <button onClick={() => setEditing(p)} data-testid="program-edit" className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setConfirmDel(p)} data-testid="program-delete" className="rounded-lg border border-slate-200 p-2 text-red-500 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ProgramModal record={editing === "new" ? empty : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="program-delete-confirm">
            <Trash2 className="mx-auto mb-3 h-10 w-10 text-red-500" />
            <h3 className="text-lg font-bold text-slate-900">Delete this program?</h3>
            <p className="mb-5 text-sm text-slate-500">&ldquo;{confirmDel.name}&rdquo; will be permanently removed. This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDel(null)} className="flex-1 rounded-lg border border-slate-300 py-2 text-sm font-medium">Cancel</button>
              <button onClick={remove} data-testid="program-delete-confirm-btn" className="flex-1 rounded-lg bg-red-500 py-2 text-sm font-medium text-white">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgramField({ label, value, onChange, testId, ...rest }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm text-slate-600">
      {label}
      <input value={value} onChange={onChange} data-testid={testId} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" {...rest} />
    </label>
  );
}

function ProgramModal({ record, onClose, onSaved }) {
  const [form, setForm] = useState({ ...record, curriculum: (record.curriculum || []).join(", "), eligibility: (record.eligibility || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.name.trim() || !form.slug.trim()) return toast.error("Name and slug are required.");
    setSaving(true);
    const body = {
      name: form.name, slug: form.slug, category: form.category, duration: form.duration,
      mode: form.mode, fee: form.fee, feeNote: form.feeNote, active: !!form.active,
      curriculum: form.curriculum.split(",").map((s) => s.trim()).filter(Boolean),
      eligibility: form.eligibility.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (record.id) await updateProgram(record.id, body);
      else await createProgram(body);
      toast.success("Saved.");
      onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const field = (label, k, rest = {}) => (
    <ProgramField label={label} value={form[k] || ""} onChange={set(k)} testId={`program-field-${k}`} {...rest} />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="program-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900">{record.id ? "Edit program" : "Add program"}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {field("Name", "name")}
          {field("Slug", "slug", { placeholder: "e.g. 3-month-internship" })}
          <label className="flex flex-col gap-1.5 text-sm text-slate-600">
            Category
            <select value={form.category} onChange={set("category")} data-testid="program-field-category" className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange">
              <option value="program">Program</option>
              <option value="internship">Internship</option>
            </select>
          </label>
          {field("Duration", "duration")}
          {field("Mode", "mode")}
          {field("Fee", "fee")}
          {field("Fee note", "feeNote")}
          <label className="mt-6 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={!!form.active} onChange={set("active")} data-testid="program-field-active" className="h-4 w-4 accent-orange" /> Active
          </label>
        </div>
        <label className="mt-4 flex flex-col gap-1.5 text-sm text-slate-600">
          Curriculum (comma-separated)
          <textarea value={form.curriculum} onChange={set("curriculum")} rows={2} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
        </label>
        <label className="mt-4 flex flex-col gap-1.5 text-sm text-slate-600">
          Eligibility (comma-separated)
          <textarea value={form.eligibility} onChange={set("eligibility")} rows={2} className="rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-orange" />
        </label>
        <button onClick={save} disabled={saving} data-testid="program-save" className="mt-5 w-full rounded-lg bg-orange py-2.5 font-medium text-white hover:opacity-90 disabled:opacity-60">
          {saving ? "Saving…" : "Save program"}
        </button>
      </div>
    </div>
  );
}
